import { ReactNode, useState } from "react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { MessageCircle, Send, Mail, Star, Calendar } from "lucide-react";
import CalendlyModal from "./CalendlyModal";

interface SectionProps {
  children: ReactNode;
  className?: string;
  id?: string;
}

export function Section({ children, className = "", id }: SectionProps) {
  return (
    <section id={id} className={cn("py-20 lg:py-28", className)}>
      <div className="container mx-auto px-4 lg:px-8">{children}</div>
    </section>
  );
}

export function SectionHeader({ badge, title, description }: { badge?: string; title: string; description?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.5 }}
      className="mb-14 max-w-2xl"
    >
      {badge && (
        <span className="mb-4 inline-block rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-medium text-primary">
          {badge}
        </span>
      )}
      <h2 className="text-3xl font-bold tracking-tight text-foreground lg:text-4xl">{title}</h2>
      {description && <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{description}</p>}
    </motion.div>
  );
}

export function TerminalWindow({ children, title = "terminal" }: { children: ReactNode; title?: string }) {
  return (
    <div className="terminal-window animate-float">
      <div className="terminal-header">
        <div className="terminal-dot bg-red-500" />
        <div className="terminal-dot bg-yellow-500" />
        <div className="terminal-dot bg-green-500" />
        <span className="ml-2 text-xs text-muted-foreground">{title}</span>
      </div>
      <div className="p-4 text-xs leading-relaxed lg:text-sm">{children}</div>
    </div>
  );
}

// ─── Testimonials ───────────────────────────────────────────────────────────
const testimonials = [
  {
    quote: "OnePix is a very professional and highly technically capable company. All requests are investigated quickly and there is an excellent project management system. I am always confident that they will deliver the project!",
    client: "Client, UK",
    tag: "eCommerce / WooCommerce",
  },
  {
    quote: "The quality of work is always high. The detail of care taken when reading and replying to our requirements and questions is always an excellent standard. After our successful collaborations, we trust the capability of the work OnePix can provide. Everyone we have communicated with in OnePix are professional, polite and friendly.",
    client: "Client, UK",
    tag: "Shopify / Fashion",
  },
  {
    quote: "The collaboration is respectful, honest and friendly. I appreciate that software errors are looked at immediately in emergencies.",
    client: "Client, Europe",
    tag: "WordPress / Subscription SaaS",
  },
  {
    quote: "With your support and hard work, it has enabled us to offer a website full of features and complex attributes that are usually only available to big companies. I am pleased to have such reliable partners!",
    client: "Client, UK",
    tag: "Custom Platform / B2B",
  },
  {
    quote: "Your solutions are excellent as usual. They work like a charm!",
    client: "Long-term client",
    tag: "WooCommerce / Retail",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

function StarRating() {
  return (
    <div className="flex gap-1">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className="h-3.5 w-3.5 fill-[hsl(45,93%,47%)] text-[hsl(45,93%,47%)]" />
      ))}
    </div>
  );
}

/** Full testimonials carousel — for Homepage & About */
export function TestimonialsSection() {
  return (
    <Section>
      <SectionHeader
        title="What clients say after working with us"
        description="86% of our clients stay with us for 2+ years. Here's why."
      />
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {testimonials.map((t, i) => (
          <motion.div
            key={i}
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: i * 0.08 }}
            className="glass-card-glow relative overflow-hidden p-6"
          >
            <span className="absolute -top-2 left-4 text-5xl font-bold gradient-text select-none leading-none opacity-30">"</span>
            <div className="relative pt-4">
              <StarRating />
              <p className="mt-3 text-sm text-foreground/90 leading-relaxed italic">"{t.quote}"</p>
              <div className="mt-4 border-t border-border/30 pt-3">
                <p className="text-sm font-medium text-foreground">{t.client}</p>
                <p className="text-xs text-muted-foreground">{t.tag}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/** Single testimonial quote — for service pages */
export function TestimonialQuote({ index = 0 }: { index?: number }) {
  const t = testimonials[index % testimonials.length];
  return (
    <Section className="py-12 lg:py-16">
      <motion.div {...fadeUp} className="glass-card-glow relative mx-auto max-w-3xl overflow-hidden p-8">
        <span className="absolute -top-2 left-6 text-6xl font-bold gradient-text select-none leading-none opacity-30">"</span>
        <div className="relative pt-4">
          <StarRating />
          <p className="mt-4 text-base text-foreground/90 leading-relaxed italic">"{t.quote}"</p>
          <div className="mt-5 flex items-center gap-3">
            <div>
              <p className="text-sm font-medium text-foreground">{t.client}</p>
              <p className="text-xs text-muted-foreground">{t.tag}</p>
            </div>
          </div>
        </div>
      </motion.div>
    </Section>
  );
}

/** Reusable Book a Call button */
export function BookCallButton({ className = "" }: { className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-6 text-sm font-medium text-primary shadow-lg shadow-primary/10 transition-all hover:bg-primary/10 hover:shadow-primary/20 ${className}`}
      >
        <Calendar className="h-4 w-4" /> Book a Free Call
      </button>
      <CalendlyModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function CTABlock({
  title = "Ready to build something that works?",
  description = "Let's talk about your project. We'll suggest the best technical path within your budget.",
}: {
  title?: string;
  description?: string;
}) {
  const [calendlyOpen, setCalendlyOpen] = useState(false);

  return (
    <Section>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="glass-card-glow gradient-border mx-auto max-w-3xl p-10 text-center lg:p-14"
      >
        <h2 className="text-2xl font-bold text-foreground lg:text-3xl">{title}</h2>
        <p className="mx-auto mt-4 max-w-lg text-muted-foreground">{description}</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={() => setCalendlyOpen(true)}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-6 text-sm font-medium text-primary shadow-lg shadow-primary/10 transition-all hover:bg-primary/10 hover:shadow-primary/20"
          >
            <Calendar className="h-4 w-4" /> Book a Free Call
          </button>
          <a href="https://wa.me/message/7LSTVCZDQZ27H1" target="_blank" rel="noopener noreferrer">
            <button className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-[hsl(142,70%,40%)] px-6 text-sm font-medium text-white shadow-lg shadow-[hsl(142,70%,40%)]/25 transition-all hover:bg-[hsl(142,70%,45%)]">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </button>
          </a>
          <a href="https://t.me/onepixteam" target="_blank" rel="noopener noreferrer">
            <button className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-primary px-6 text-sm font-medium text-primary-foreground shadow-lg shadow-primary/25 transition-all hover:bg-primary/90">
              <Send className="h-4 w-4" /> Telegram
            </button>
          </a>
          <a href="mailto:info@onepix.net">
            <button className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-border bg-transparent px-6 text-sm font-medium text-foreground transition-all hover:bg-muted/50">
              <Mail className="h-4 w-4" /> Send Email
            </button>
          </a>
        </div>
      </motion.div>
      <CalendlyModal open={calendlyOpen} onClose={() => setCalendlyOpen(false)} />
    </Section>
  );
}
